/**
 * Cookie Consent
 *
 * Manages the user's cookie consent state in localStorage. PostHog
 * analytics are only initialized when consent is 'accepted'. If the
 * user has not made a choice or has declined, PostHog never loads —
 * no scripts, no cookies, no network requests.
 *
 * GDPR requires consent BEFORE analytics tracking begins. This module
 * is the single source of truth for that decision.
 */

import posthog from 'posthog-js'

const STORAGE_KEY = 'bidcontext-cookie-consent'

export type ConsentStatus = 'accepted' | 'declined'

/**
 * Read the current consent status from localStorage.
 * Returns null if the user hasn't made a choice yet.
 */
export function getConsent(): ConsentStatus | null {
  try {
    const value = localStorage.getItem(STORAGE_KEY)
    if (value === 'accepted' || value === 'declined') return value
    return null
  } catch {
    // localStorage may be unavailable (e.g. private browsing)
    return null
  }
}

/**
 * Whether the user has made ANY choice (accepted or declined).
 * When false, the consent banner should be shown.
 */
export function hasConsented(): boolean {
  return getConsent() !== null
}

/**
 * Whether analytics tracking is allowed.
 * Only true when the user has explicitly accepted.
 */
export function isAnalyticsAllowed(): boolean {
  return getConsent() === 'accepted'
}

/**
 * Record the user's consent choice. If they accept, PostHog is
 * initialized immediately (so analytics capture begins mid-session).
 * If they decline (or revoke a previous acceptance), PostHog is
 * shut down and its cookies/localStorage entries are cleared.
 */
export function setConsent(status: ConsentStatus): void {
  try {
    localStorage.setItem(STORAGE_KEY, status)
  } catch {
    // Silently fail if localStorage is unavailable
  }

  if (status === 'accepted') {
    // Dynamically initialize PostHog if not already running.
    // Import is static but initPostHog() is idempotent.
    import('@/lib/posthog').then(({ initPostHog }) => {
      initPostHog()
    }).catch(() => {})
  } else {
    // Revoke: shut down PostHog and clear its data
    try {
      posthog.opt_out_capturing()
      posthog.reset()
    } catch {
      // PostHog may not be initialized — safe to ignore
    }
  }
}

/**
 * Clear the consent choice entirely. Used for testing or when resetting
 * user state. After this call, the banner will reappear.
 */
export function clearConsent(): void {
  try {
    localStorage.removeItem(STORAGE_KEY)
  } catch {
    // Silently fail
  }
}
